import { useEffect } from "react";

import { useKpiDatasetKind } from "./useKpiDatasetKind";
import type { XFactorKpiFullBriefing } from "../Pages/TempViewsRendering/lib/deriveKpi";
import { useAppDispatch, useAppSelector } from "../Store/Hooks";
import {
  fetchKpiFullBriefing,
  kpiAccountBriefingKey,
} from "../Store/Reducers/XFactorKpiSlice";

export type XFactorKpiFullBriefingState = {
  briefing: XFactorKpiFullBriefing | null;
  loading: boolean;
  error: string | null;
};

/**
 * Full account briefing (render/kpi view=full) — every category plus
 * overall in one call, so the category detail modal's Highlights/Lowlights/
 * Opportunities/Risks/Actions/Trends and its explainability panel can
 * switch categories client-side without refetching. Additive alongside
 * useXFactorKpiAccountBriefing (view=category, still used by the compact
 * Situation Card) — this powers the richer bubble click-through modal.
 *
 * `measurementDate` is an optional exact-date override; omit it to let
 * xfactor resolve the newest briefed date itself (live as of 2026-09-21).
 * Data lives in XFactorKpiSlice.
 */
export const useXFactorKpiFullBriefing = (
  accountId?: string | null,
  measurementDate?: string | null
): XFactorKpiFullBriefingState => {
  const dispatch = useAppDispatch();
  const datasetKind = useKpiDatasetKind();
  const key = accountId
    ? kpiAccountBriefingKey({ accountId, datasetKind, measurementDate })
    : null;
  const entry = useAppSelector(state =>
    key ? state.xfactorKpi.fullBriefings[key] : undefined
  );

  useEffect(() => {
    if (!accountId) return;
    dispatch(fetchKpiFullBriefing({ accountId, datasetKind, measurementDate }));
  }, [dispatch, accountId, measurementDate, datasetKind]);

  return {
    briefing: entry?.briefing ?? null,
    loading: entry ? entry.loading : Boolean(accountId),
    error: entry?.error ?? null,
  };
};

export default useXFactorKpiFullBriefing;
